"use client";

import {
    useCallback,
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import { io, Socket } from "socket.io-client";
import { useMessages } from "@me8eon/sessions_messages";
import { useSearchTextFilter } from "@me8eon/searchtext_filters";
import { useDoAi } from "@me8eon/react_intent";
import useSearchStore from "../../store/searchStore";
import { EnvironmentConfig } from "@me8eon/config";


type SpeechRecognitionEventResult = {
    results: {
        length: number;
        [index: number]: {
            isFinal?: boolean;
            [index: number]: {
                transcript: string;
            };
        };
    };
};

type SpeechRecognitionInstance = {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onstart: (() => void) | null;
    onresult: ((event: SpeechRecognitionEventResult) => void) | null;
    onerror: ((event: unknown) => void) | null;
    onend: (() => void) | null;
    start: () => void;
    stop: () => void;
    abort: () => void;
};

declare global {
    interface Window {
        SpeechRecognition?: new () => SpeechRecognitionInstance;
        webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    }
}

export type GeminiAssistantVoiceRef = {
    startListening: () => void;
    stopListening: () => void;
};

const GeminiAssistantVoice = forwardRef<GeminiAssistantVoiceRef>(
    (_, ref) => {
        const [messages] = useMessages();

        const socketRef = useRef<Socket | null>(null);
        const audioRef = useRef<HTMLAudioElement | null>(null);
        const audioContextRef = useRef<AudioContext | null>(null);
        const nextPlayTimeRef = useRef(0);
        const lastSpokenRef = useRef("");
        const voiceEnabledRef = useRef(false);
        const speakingTimeoutRef = useRef<number | null>(null);

        const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
        const shouldListenRef = useRef(false);
        const isAiSpeakingRef = useRef(false);
        const isRecognitionRunningRef = useRef(false);

        const [voiceEnabled, setVoiceEnabled] = useState(false);
        const [isAiSpeaking, setIsAiSpeaking] = useState(false);
        const [isListening, setIsListening] = useState(false);
        const [, setQuery] = useSearchTextFilter();
        const [newText, setNewText] = useState("");
        const handleAiQuery = useDoAi();
        const { clearQuestionData } = useSearchStore();

        const submitTextArea = useCallback(
            (text: string) => {
                const finalText = text.trim();

                if (!finalText) return;

                handleAiQuery(finalText);
                setQuery("");
                clearQuestionData();
            },
            [handleAiQuery, setQuery, clearQuestionData],
        );

        const stopListening = useCallback(() => {
            shouldListenRef.current = false;

            try {
                recognitionRef.current?.stop();
            } catch (error) {
                console.error("Stop recognition error:", error);
            }

            setIsListening(false);
        }, []);

        const startListening = useCallback(() => {
            const SpeechRecognition =
                window.SpeechRecognition || window.webkitSpeechRecognition;

            if (!SpeechRecognition) {
                console.error("Speech recognition is not supported in this browser.");
                return;
            }

            shouldListenRef.current = true;

            if (!recognitionRef.current) {
                const recognition = new SpeechRecognition();

                recognition.continuous = true;
                recognition.interimResults = false;
                recognition.lang = "en-US";

                recognition.onstart = () => {
                    isRecognitionRunningRef.current = true;
                    setIsListening(true);
                    console.log("Mic started.");
                };

                recognition.onresult = (event: SpeechRecognitionEventResult) => {
                    if (isAiSpeakingRef.current) return;

                    const lastIndex = event.results.length - 1;
                    const text = event.results[lastIndex][0].transcript.trim();

                    if (!text) return;

                    console.log("Recognized text:", text);

                    setNewText(text);
                    submitTextArea(text);
                };

                recognition.onerror = (event: unknown) => {
                    console.error("Speech recognition error:", event);
                    isRecognitionRunningRef.current = false;
                    setIsListening(false);
                };

                recognition.onend = () => {
                    console.log("Mic stopped.");

                    isRecognitionRunningRef.current = false;
                    setIsListening(false);

                    if (shouldListenRef.current && !isAiSpeakingRef.current) {
                        window.setTimeout(() => {
                            try {
                                recognition.start();
                            } catch {
                                // Browser may throw if already started.
                            }
                        }, 400);
                    }
                };

                recognitionRef.current = recognition;
            }

            if (isAiSpeakingRef.current || isRecognitionRunningRef.current) return;

            try {
                recognitionRef.current.start();
            } catch {
                // Browser may throw if already started.
            }
        }, [submitTextArea]);

        useImperativeHandle(ref, () => ({
            startListening,
            stopListening,
        }));

        async function enableVoice() {
            if (voiceEnabledRef.current) return;
            if (!audioRef.current) return;

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext({
                    sampleRate: 24000,
                });
            }

            await audioContextRef.current.resume();

            voiceEnabledRef.current = true;
            setVoiceEnabled(true);
        }

        async function playPcm24k(base64Audio: string) {
            if (!voiceEnabledRef.current) {
                await enableVoice();
            }

            const audioContext = audioContextRef.current;
            if (!audioContext) return;

            if (audioContext.state === "suspended") {
                await audioContext.resume();
            }

            if (recognitionRef.current && isRecognitionRunningRef.current) {
                try {
                    recognitionRef.current.stop();
                } catch {
                    // Ignore stop errors.
                }
            }

            isAiSpeakingRef.current = true;

            const pcm16 = base64ToInt16Array(base64Audio);
            const float32 = new Float32Array(pcm16.length);

            for (let i = 0; i < pcm16.length; i++) {
                float32[i] = pcm16[i] / 32768;
            }

            const audioBuffer = audioContext.createBuffer(1, float32.length, 24000);
            audioBuffer.copyToChannel(float32, 0);

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);

            const now = audioContext.currentTime;

            if (nextPlayTimeRef.current < now) {
                nextPlayTimeRef.current = now;
            }

            source.start(nextPlayTimeRef.current);
            nextPlayTimeRef.current += audioBuffer.duration;

            setIsAiSpeaking(true);

            if (speakingTimeoutRef.current) {
                window.clearTimeout(speakingTimeoutRef.current);
            }

            const remainingMs = Math.max(
                300,
                (nextPlayTimeRef.current - audioContext.currentTime) * 1000 + 250,
            );

            speakingTimeoutRef.current = window.setTimeout(() => {
                isAiSpeakingRef.current = false;
                setIsAiSpeaking(false);

                if (shouldListenRef.current) {
                    startListening();
                }
            }, remainingMs);
        }

        function base64ToInt16Array(base64: string) {
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            return new Int16Array(bytes.buffer);
        }

        useEffect(() => {
            const socket = io(`${EnvironmentConfig.nodeService}`, {
                path: "/service/geminiaudio",
                transports: ["websocket"],
            });

            socketRef.current = socket;

            socket.on("connect", () => {
                console.log("Voice socket connected", socket.id);
            });

            socket.on("gemini:audio", async (data) => {
                await playPcm24k(data.audio);
            });

            socket.on("gemini:error", console.error);

            return () => {
                voiceEnabledRef.current = false;
                shouldListenRef.current = false;

                if (speakingTimeoutRef.current) {
                    window.clearTimeout(speakingTimeoutRef.current);
                }

                try {
                    recognitionRef.current?.abort();
                } catch {
                    // Ignore abort errors.
                }

                socket.disconnect();
                audioContextRef.current?.close().catch(console.error);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            const unlockVoice = async () => {
                await enableVoice();
            };

            window.addEventListener("pointerdown", unlockVoice, { once: true });
            window.addEventListener("keydown", unlockVoice, { once: true });

            return () => {
                window.removeEventListener("pointerdown", unlockVoice);
                window.removeEventListener("keydown", unlockVoice);
            };
        }, []);

        useEffect(() => {
            const lastMsgObj = messages[messages.length - 1];

            if (
                !lastMsgObj ||
                lastMsgObj.role !== "assistant" ||
                window.location.pathname !== "/vocal-ai"
            ) {
                return;
            }

            const userMsg = String(lastMsgObj.content || "").trim();
            if (!userMsg) return;

            const text = `You are a voice assistant summarizer.

Summarize the following content into a short, clear, natural spoken response.

Rules:
- Return ONLY the summary.
- Do NOT add introductions, acknowledgements, conclusions, or commentary.
- Do NOT say phrases like "Here is the summary", "Sure", "Okay", or "The text says".
- Do NOT use quotes or bullet points.
- Keep the response conversational and concise.
- Preserve the original meaning without adding new information.

Content:
${userMsg}`;

            if (lastSpokenRef.current === text) return;

            lastSpokenRef.current = text;
            nextPlayTimeRef.current = 0;

            socketRef.current?.emit("client:text", { text });
        }, [messages]);

        if (typeof window === "undefined") return null;

        if (window.location.pathname !== "/vocal-ai") {
            return null;
        }

        return (
            <div>
                <audio ref={audioRef} hidden />

                {newText && <p>Recognized: {newText}</p>}

                {voiceEnabled && isAiSpeaking && (
                    <div className="ai-voice-wave" aria-label="AI speaking">
                        {Array.from({ length: 18 }).map((_, index) => (
                            <span
                                key={index}
                                style={{
                                    animationDelay: `${index * 0.06}s`,
                                }}
                            />
                        ))}
                    </div>
                )}

                <style>{`
                .ai-voice-wave {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    height: 42px;
                    padding: 0 16px;
                    border-radius: 999px;
                    background: color-mix(in srgb, var(--primary-color) 10%, transparent);
                    border: 1px solid color-mix(in srgb, var(--primary-color) 18%, transparent);
                    backdrop-filter: blur(12px);
                    box-shadow:
                        0 4px 20px color-mix(in srgb, var(--primary-color) 12%, transparent),
                        inset 0 1px 0 rgba(255, 255, 255, 0.45);
                }

                .ai-voice-wave span {
                    width: 5px;
                    height: 14px;
                    border-radius: 999px;
                    background: linear-gradient(
                        180deg,
                        color-mix(in srgb, white 45%, var(--primary-color)),
                        color-mix(in srgb, white 15%, var(--primary-color)),
                        var(--primary-color)
                    );
                    animation: aiWave 0.9s ease-in-out infinite;
                    box-shadow:
                        0 0 10px color-mix(in srgb, var(--primary-color) 35%, transparent),
                        0 0 20px color-mix(in srgb, var(--primary-color) 18%, transparent);
                }

                @keyframes aiWave {
                    0%,
                    100% {
                        transform: scaleY(0.35);
                        opacity: 0.55;
                    }

                    50% {
                        transform: scaleY(2.2);
                        opacity: 1;
                    }
                }
            `}</style>
            </div>
        );
    },
);

GeminiAssistantVoice.displayName = "GeminiAssistantVoice";

export default GeminiAssistantVoice;